import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { FileText, Download, Edit, Trash2, Upload, FolderOpen } from 'lucide-react';
import usePermissions from '../hooks/usePermissions';
import Navbar from '../components/Navbar';
import PermissionDeniedCard from '../components/PermissionDeniedCard';

const API_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.acbase.org').replace(/\/$/, '');

export default function ArchivePage() {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form data per upload
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategoryId, setUploadCategoryId] = useState('');
  
  // Form data per edit
  const [editTitle, setEditTitle] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  
  const canView = hasPermission('ARCHIVE_VIEW');
  const canCreate = hasPermission('ARCHIVE_CREATE');
  const canEditAll = hasPermission('ARCHIVE_EDIT_ALL');
  const canEditOwn = hasPermission('ARCHIVE_EDIT_OWN');
  const canDeleteAll = hasPermission('ARCHIVE_DELETE_ALL');
  const canDeleteOwn = hasPermission('ARCHIVE_DELETE_OWN');
  
  // Helper functions per controllare i permessi
  const canEditDocument = (doc) => {
    return canEditAll || (canEditOwn && doc.is_own);
  };
  
  const canDeleteDocument = (doc) => {
    return canDeleteAll || (canDeleteOwn && doc.is_own);
  };

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }

    if (canView) {
      fetchCategories();
      fetchDocuments();
    }
  }, [canView, permissionsLoading]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/archive/categories`, {
        headers: getAuthHeader()
      });
      const orderedCategories = [...res.data].sort((a, b) => {
        const left = Number.isFinite(a.sort_order) ? a.sort_order : 0;
        const right = Number.isFinite(b.sort_order) ? b.sort_order : 0;

        if (left !== right) {
          return left - right;
        }

        return a.id - b.id;
      });
      setCategories(orderedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      alert('Errore nel caricamento delle categorie: ' + getErrorMessage(error));
    }
  };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/archive/documents`, {
        headers: getAuthHeader()
      });
      setDocuments(res.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      alert('Errore nel caricamento dei documenti: ' + getErrorMessage(error));
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle || !uploadCategoryId) {
      alert('Compila tutti i campi');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);
      formData.append('category_id', uploadCategoryId);
      
      await axios.post(`${API_URL}/api/archive/documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert('Documento caricato con successo!');
      setIsUploadModalOpen(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadCategoryId('');
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Errore durante il caricamento: ' + getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/api/archive/documents/${documentId}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Crea un link temporaneo per scaricare il file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Errore durante il download: ' + getErrorMessage(error));
    }
  };

  const openEditModal = (doc) => {
    setSelectedDocument(doc);
    setEditTitle(doc.title);
    setEditCategoryId(doc.category_id?.toString() || '');
    setIsEditModalOpen(true);
  };

  const handleEdit = async () => {
    if (!editTitle || !editCategoryId) {
      alert('Compila tutti i campi');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', editTitle);
      formData.append('category_id', editCategoryId);
      
      await axios.put(`${API_URL}/api/archive/documents/${selectedDocument.id}`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert('Documento aggiornato con successo!');
      setIsEditModalOpen(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error updating document:', error);
      alert('Errore durante l\'aggiornamento: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId, filename) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${filename}"?`)) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/archive/documents/${documentId}`, {
        headers: getAuthHeader()
      });
      
      alert('Documento eliminato con successo!');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Errore durante l\'eliminazione: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT') + ' ' + date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const renderDocumentRow = (doc, iconClassName) => (
    <div
      key={doc.id}
      className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-colors hover:bg-slate-50 sm:flex-row sm:items-start sm:justify-between"
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <FileText className={`mt-1 h-5 w-5 shrink-0 ${iconClassName}`} />
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-sm font-semibold text-slate-900 sm:text-base">{doc.title}</h3>
          <div className="mt-2 grid gap-x-3 gap-y-1 text-xs text-slate-500 sm:grid-cols-2 sm:text-sm lg:grid-cols-4">
            <span className="break-all">{doc.filename}</span>
            <span>{formatFileSize(doc.file_size)}</span>
            <span className="break-words">Caricato da {doc.uploader_name}</span>
            <span>{formatDate(doc.upload_date)}</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 self-start sm:shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleDownload(doc.id, doc.filename)}
          title="Scarica"
        >
          <Download className="h-4 w-4" />
        </Button>
        {canEditDocument(doc) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditModal(doc)}
            title="Modifica"
          >
            <Edit className="h-4 w-4" />
          </Button>
        )}
        {canDeleteDocument(doc) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(doc.id, doc.filename)}
            title="Elimina"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderCategoryCard = ({ id, title, description, documents, iconClassName, accentClassName }) => (
    <Card key={id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white/95 shadow-sm">
      <Accordion type="single" collapsible>
        <AccordionItem value={`category-${id}`} className="border-b-0">
          <CardHeader className={`p-0 ${accentClassName}`}>
            <AccordionTrigger className="px-4 py-4 text-left hover:no-underline sm:px-6 sm:py-5">
              <div className="flex w-full min-w-0 items-start gap-3 sm:gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 shadow-sm ring-1 ring-slate-200/70">
                  <FolderOpen className={`h-5 w-5 ${iconClassName}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="min-w-0">
                    <CardTitle className="truncate text-base font-semibold text-slate-900 sm:text-lg">
                      {title}
                    </CardTitle>
                    <CardDescription className="mt-1 text-sm leading-relaxed text-slate-600">
                      {description || 'Documentazione disponibile in questa sezione dell’archivio.'}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </AccordionTrigger>
          </CardHeader>
          <AccordionContent>
            <CardContent className="border-t border-slate-200 bg-slate-50/70 p-4 sm:p-6">
              <div className="space-y-3">
                {documents.map(doc => renderDocumentRow(doc, iconClassName))}
              </div>
            </CardContent>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );

  // Raggruppa documenti per categoria
  const documentsByCategory = categories.map(category => ({
    ...category,
    documents: documents.filter(doc => doc.category_id === category.id)
  }));

  // Documenti senza categoria
  const uncategorizedDocs = documents.filter(doc => !doc.category_id);

  if (permissionsLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="p-6 pt-20">Caricamento...</div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <PermissionDeniedCard message="Non hai i permessi per visualizzare l'archivio." />
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="mx-auto max-w-7xl p-4 pt-20 sm:p-6 sm:pt-24">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen className="h-6 w-6" />
              <h1 className="text-2xl font-bold sm:text-3xl">Archivio documenti</h1>
            </div>
            <p className="text-gray-600 text-sm sm:text-base">
              Qui è raccolta e organizzata tutta la documentazione dell’Associazione: statuto, regolamenti, brochure istituzionali, materiali informativi, fotografie delle attività e degli eventi (incluse le cene associative), nonché ogni altro contenuto utile alla vita e alla memoria di Base.
            </p>
          </div>
          {canCreate && (
            <Button className="w-full sm:w-auto" onClick={() => setIsUploadModalOpen(true)}>
              <Upload className="mr-2 h-4 w-4" /> Carica documento
            </Button>
          )}
        </div>

        {/* Documenti raggruppati per categoria */}
        <div className="space-y-6">
          {documentsByCategory.map(category => (
            category.documents.length > 0 && (
              renderCategoryCard({
                id: category.id,
                title: category.name,
                description: category.description,
                documents: category.documents,
                iconClassName: 'text-blue-600',
                accentClassName: 'bg-gradient-to-r from-sky-50 via-white to-slate-50',
              })
            )
          ))}

          {/* Documenti senza categoria */}
          {uncategorizedDocs.length > 0 && (
            renderCategoryCard({
              id: 'uncategorized',
              title: 'Senza Categoria',
              description: 'Documenti presenti in archivio ma non ancora assegnati a una tipologia specifica.',
              documents: uncategorizedDocs,
              iconClassName: 'text-slate-500',
              accentClassName: 'bg-gradient-to-r from-slate-100 via-white to-slate-50',
            })
          )}

          {documents.length === 0 && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-gray-500">
                  <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun documento presente nell'archivio</p>
                  {canCreate && (
                    <Button className="mt-4" onClick={() => setIsUploadModalOpen(true)}>
                      Carica il primo documento
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Modal Upload */}
        <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Carica documento</DialogTitle>
              <DialogDescription>
                Seleziona un file e compila i dettagli
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="file">File</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Titolo</Label>
                <Input
                  id="title"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Inserisci un titolo descrittivo"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Select
                  value={uploadCategoryId}
                  onValueChange={setUploadCategoryId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsUploadModalOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleUpload} disabled={loading}>
                {loading ? 'Caricamento...' : 'Carica'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Edit */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifica Documento</DialogTitle>
              <DialogDescription>
                Aggiorna i dettagli del documento
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Titolo</Label>
                <Input
                  id="edit-title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-category">Categoria</Label>
                <Select
                  value={editCategoryId}
                  onValueChange={setEditCategoryId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleEdit} disabled={loading}>
                {loading ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
